<script setup lang="ts">
/**
 * Вход — макет 25:1203: логотип 144×36, карточка 360×404 с отступом 32,
 * поля 296×40, кнопка «Войти», разделитель «или» и вход через Google.
 *
 * Кнопки Google в бэкенде пока нет ни одной ручки, поэтому она появляется
 * только когда задан VITE_SSO_URL: мёртвая кнопка на экране входа хуже её
 * отсутствия — человек жмёт и не понимает, почему ничего не происходит.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Eye, EyeOff, User } from 'lucide-vue-next'
import { AppButton } from '@vinprop/design-system'
import { isAxiosError } from 'axios'

import logoDark from '@/assets/vinprop-tracker-logo-white.svg'
import logoLight from '@/assets/vinprop-tracker-logo.svg'
import { useAuthStore } from '@/features/auth/store'
import { useTheme } from '@vinprop/design-system'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { isDark } = useTheme()

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const error = ref('')

const ssoUrl = import.meta.env.VITE_SSO_URL as string | undefined

const logo = computed(() => (isDark.value ? logoDark : logoLight))

const submit = async () => {
  error.value = ''

  try {
    await auth.login(email.value.trim(), password.value)

    const redirect = route.query.redirect
    await router.replace(typeof redirect === 'string' && redirect ? redirect : '/my')
  } catch (err) {
    // 401 — неверная пара; всё остальное сеть или сервис, и валить это на
    // человека («проверьте пароль») было бы враньём.
    error.value = isAxiosError(err) && err.response?.status === 401
      ? t('login.errors.invalid')
      : t('login.errors.unavailable')
  }
}
</script>

<template>
  <div class="login">
    <img class="login__logo" :src="logo" alt="VINPROP Tracker" width="144" height="36" />
    <p class="login__product">{{ t('app.name') }}</p>

    <form class="login__card" @submit.prevent="submit">
      <h1 class="login__title">{{ t('login.title') }}</h1>

      <label class="login__label" for="login-email">{{ t('login.email') }}</label>
      <div class="login__field">
        <User class="login__field-icon" :size="16" />
        <input
          id="login-email"
          v-model="email"
          class="login__input"
          type="email"
          name="email"
          autocomplete="username"
          required
          :placeholder="t('login.emailPlaceholder')"
        />
      </div>

      <label class="login__label" for="login-password">{{ t('login.password') }}</label>
      <div class="login__field">
        <button
          type="button"
          class="login__field-icon login__field-icon--button"
          :aria-label="t(showPassword ? 'login.hidePassword' : 'login.showPassword')"
          @click="showPassword = !showPassword"
        >
          <EyeOff v-if="showPassword" :size="16" />
          <Eye v-else :size="16" />
        </button>
        <input
          id="login-password"
          v-model="password"
          class="login__input"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          autocomplete="current-password"
          required
          :placeholder="t('login.passwordPlaceholder')"
        />
      </div>

      <p v-if="error" class="login__error" role="alert">{{ error }}</p>

      <AppButton
        class="login__submit"
        type="submit"
        variant="primary"
        full-width
        :loading="auth.isLoading"
      >
        {{ t('login.submit') }}
      </AppButton>

      <template v-if="ssoUrl">
        <div class="login__divider"><span>{{ t('login.or') }}</span></div>

        <a class="login__sso" :href="ssoUrl">{{ t('login.google') }}</a>
      </template>
    </form>

    <p class="login__footer">{{ t('login.footer') }}</p>
  </div>
</template>

<style scoped>
.login {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 197px 16px 32px;
  background-color: var(--color-bg-app);
}

.login__logo {
  width: 144px;
  height: 36px;
}

.login__product {
  margin: 24px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
}

.login__card {
  display: flex;
  flex-direction: column;
  width: 360px;
  max-width: 100%;
  margin-top: 24px;
  padding: 32px;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

/* Шаг внутри карточки везде 14px, а высоты блоков взяты из макета: заголовок
   36, подпись поля 13, поле и кнопки 40 — иначе расхождение копится к низу. */
.login__title {
  height: 36px;
  margin: 0 0 14px;
  color: var(--color-text-primary);
  font-size: var(--font-size-h1);
  font-weight: 700;
  line-height: 36px;
}

.login__label {
  height: 13px;
  margin-bottom: 14px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 13px;
}

.login__field {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  margin-bottom: 14px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
}

.login__field:focus-within {
  border-color: var(--color-primary);
}

.login__field-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.login__field-icon--button {
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.login__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body);
  outline: none;
}

.login__error {
  margin: 0 0 12px;
  color: var(--color-danger);
  font-size: var(--font-size-body-sm);
}

.login__submit {
  height: 40px;
}

.login__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.login__divider::before,
.login__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--color-border);
}

.login__sso {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  text-decoration: none;
}

.login__sso:hover {
  background-color: var(--color-bg-muted);
}

.login__footer {
  margin-top: 24px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
